/**
 * Single source of truth for "who does Reply / Reply-all address?".
 *
 * Why this exists: replying to an email the owner SENT used to pre-fill the composer with the
 * owner's own address, because every opener/composer decided "sent by the owner" from one
 * brittle signal (folder === 'sent') and each re-implemented the recipient maths. In production
 * 336 of 1,265 owner-authored docs in a 200-day window are NOT folder 'sent' (own copies that
 * landed in inbox/deleted/drafts, direction 'in'), legacy IMAP-era docs have no mailbox and
 * carry {address,name} objects, and registrations@ form notifications are addressed to
 * [self, submitter] and must be answered to the submitter.
 *
 * Pure: no React, no Firestore. A server copy lives in functions/src/utils/replyRecipients.ts
 * (separate TS project, cannot import from src/) - keep the two in step.
 */
import { normalizeEmailFolder } from './emailFolder';

export type AddressLike =
  | string
  | { address?: string; email?: string; text?: string; name?: string }
  | null
  | undefined;

export interface ReplySource {
  from?: AddressLike | AddressLike[];
  senderEmail?: string;
  to?: AddressLike | AddressLike[];
  cc?: AddressLike | AddressLike[];
  mailbox?: string;
  folder?: unknown;
  deleted?: unknown;
  emailType?: unknown;
  direction?: unknown;
  quarantined?: unknown;
}

export interface ReplyRecipientsOptions {
  mode: 'reply' | 'replyAll';
  /**
   * "Us", for filtering: never put in Cc, and avoided in To while anything else is addressable.
   * Callers should pass a NARROW set (the signed-in user + the From account; the email's own
   * mailbox is always added here): a colleague's address (lidia@) in this set is silently dropped
   * from Reply-all's Cc. Without selfAddresses / accountAddresses it is also the authorship
   * evidence for docs with no mailbox (the pre-existing contract).
   */
  ownAddresses?: Iterable<string | null | undefined>;
  /**
   * The signed-in person's own address(es) - NEVER a colleague's. Mail authored by one of these is
   * owner-sent wherever its copy sits, e.g. the copy that landed in Lidia's mailbox when Ben cc'd
   * her (from != mailbox, direction 'in'): without this it reads as inbound and pre-fills Ben's
   * own address. Also treated as "us" for filtering.
   */
  selfAddresses?: Iterable<string | null | undefined>;
  /**
   * Every mailbox/account the business runs. Authorship evidence ONLY for docs with no mailbox
   * (legacy, nothing else says who wrote them), so ownAddresses can stay narrow. Defaults to
   * ownAddresses.
   */
  accountAddresses?: Iterable<string | null | undefined>;
}

type OwnershipOptions = Pick<ReplyRecipientsOptions, 'ownAddresses' | 'selfAddresses' | 'accountAddresses'>;

export interface ReplyRecipients {
  to: string[];
  cc: string[];
  /** lowercased address -> display name, for every parsed address that had one */
  names: Record<string, string>;
  /** the email was authored by the owner, so the reply continues with its recipients */
  ownSent: boolean;
  /** To still contains an own address because nothing else was addressable (self / internal mail) */
  usedOwnFallback: boolean;
}

interface ParsedAddress {
  address: string;
  name?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const lower = (s: string): string => s.trim().toLowerCase();

/** Bare address from a segment's address part: strips mailto:, quotes and stray angle brackets. */
function cleanAddress(raw: string): string {
  let s = raw.trim();
  let hadMailto = false;
  // Peel wrappers in any order ("<mailto:a@x>", "\"a@x\"") until stable.
  for (let i = 0; i < 4; i++) {
    const before = s;
    if (/^mailto:/i.test(s)) { s = s.replace(/^mailto:/i, ''); hadMailto = true; }
    s = s.replace(/^[<"'\s]+/, '').replace(/[>"'\s]+$/, '');
    if (s === before) break;
  }
  // mailto:a@x?subject=hi - the query is not part of the address.
  if (hadMailto) s = s.replace(/\?.*$/, '');
  return s;
}

function cleanName(raw: string | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined;
  let s = raw.trim();
  const m = /^(["'])([\s\S]*)\1$/.exec(s);
  if (m) s = m[2];
  s = s.replace(/\\(["\\])/g, '$1').replace(/\s+/g, ' ').trim();
  return s || undefined;
}

/** Split "a@x, b@y; c@z" at top-level , and ; only (not inside "quotes" or <angles>). */
function splitList(s: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuote = false;
  let angle = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuote && ch === '\\' && i + 1 < s.length) { cur += ch + s[i + 1]; i++; continue; }
    if (ch === '"') inQuote = !inQuote;
    else if (!inQuote) {
      if (ch === '<') angle++;
      else if (ch === '>' && angle > 0) angle--;
    }
    if (!inQuote && angle === 0 && (ch === ',' || ch === ';')) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  // An unbalanced quote would swallow the rest of the list: fall back to a plain split.
  return inQuote ? s.split(/[,;]/) : out;
}

function parseSegment(seg: string): ParsedAddress | null {
  const s = seg.trim();
  if (!s) return null;
  const m = /^([\s\S]*)<([^<>]*)>\s*$/.exec(s);
  const address = cleanAddress(m ? m[2] : s);
  if (!EMAIL_RE.test(address)) return null;
  const name = m ? cleanName(m[1]) : undefined;
  return name ? { address, name } : { address };
}

function parseString(s: string): ParsedAddress[] {
  const out: ParsedAddress[] = [];
  splitList(s).forEach(seg => {
    const p = parseSegment(seg);
    if (p) out.push(p);
  });
  return out;
}

function collect(v: unknown, out: ParsedAddress[]): void {
  if (v === null || v === undefined) return;
  if (typeof v === 'string') { parseString(v).forEach(p => out.push(p)); return; }
  if (Array.isArray(v)) { v.forEach(x => collect(x, out)); return; }
  if (typeof v !== 'object') return;
  const o = v as { address?: unknown; email?: unknown; text?: unknown; name?: unknown };
  // First field that yields a valid address wins ('text' is "Name <a@x>" on parsed-mail shapes).
  const keys: Array<'address' | 'email' | 'text'> = ['address', 'email', 'text'];
  for (let i = 0; i < keys.length; i++) {
    const raw = o[keys[i]];
    if (typeof raw !== 'string') continue;
    const parsed = parseString(raw);
    if (!parsed.length) continue;
    const objName = typeof o.name === 'string' ? cleanName(o.name) : undefined;
    parsed.forEach(p => out.push(parsed.length === 1 && !p.name && objName ? { address: p.address, name: objName } : p));
    return;
  }
}

/**
 * Every valid address in any shape we store or produce: "a@x", "Name <a@x>", "\"Last, First\" <a@x>",
 * comma/semicolon-joined lists, {address,email,text,name} objects, nested arrays, null/undefined.
 * Deduped case-insensitively keeping the first-seen casing; display names kept where known.
 */
export function parseAddresses(v: AddressLike | AddressLike[]): { address: string; name?: string }[] {
  const raw: ParsedAddress[] = [];
  collect(v, raw);
  const seen: Record<string, ParsedAddress> = {};
  const out: ParsedAddress[] = [];
  raw.forEach(p => {
    const key = lower(p.address);
    const prev = seen[key];
    if (prev) {
      // A later duplicate may carry the label the first one lacked.
      if (!prev.name && p.name && lower(p.name) !== key) prev.name = p.name;
      return;
    }
    const entry: ParsedAddress = { address: p.address };
    if (p.name && lower(p.name) !== key) entry.name = p.name;
    seen[key] = entry;
    out.push(entry);
  });
  return out;
}

function normalisedSet(list: Iterable<string | null | undefined> | undefined): Set<string> {
  const out = new Set<string>();
  if (!list) return out;
  // A bare string is an Iterable<string> to the type system: without this it would be split into
  // characters and silently yield an empty set (every own address then reads as someone else).
  (typeof list === 'string' ? [list as string] : Array.from(list)).forEach(a => {
    if (typeof a !== 'string') return;
    parseAddresses(a).forEach(p => out.add(lower(p.address)));
  });
  return out;
}

function mailboxOf(src: ReplySource): string {
  const first = parseAddresses(typeof src.mailbox === 'string' ? src.mailbox : undefined)[0];
  return first ? lower(first.address) : '';
}

function rawFolderIsSent(folder: unknown): boolean {
  if (typeof folder !== 'string') return false;
  const f = lower(folder);
  return f === 'sent' || f === 'inbox.sent';
}

interface OwnSets {
  mailbox: string;
  /** "us" for filtering: ownAddresses + selfAddresses + the doc's mailbox */
  own: Set<string>;
  /** the signed-in person only */
  self: Set<string>;
  /** authorship evidence for docs with no mailbox */
  accounts: Set<string>;
}

function ownSets(src: ReplySource, o: OwnershipOptions): OwnSets {
  const mailbox = mailboxOf(src);
  const own = normalisedSet(o.ownAddresses);
  const self = normalisedSet(o.selfAddresses);
  self.forEach(a => own.add(a));
  // Taken before the mailbox joins: with no mailbox it is the same set either way.
  const accounts = o.accountAddresses ? normalisedSet(o.accountAddresses) : new Set<string>(Array.from(own));
  if (mailbox) {
    own.add(mailbox);
  } else {
    // No mailbox: the author's own account plays the mailbox's role, so it is "us" for filtering
    // even when ownAddresses is narrow (a form notification from registrations@ to
    // [registrations@, submitter] must answer the submitter only).
    const from = parseAddresses(src.from);
    if (from.length > 0 && from.every(p => accounts.has(lower(p.address)) || self.has(lower(p.address)))) {
      from.forEach(p => own.add(lower(p.address)));
    }
  }
  return { mailbox, own, self, accounts };
}

function computeOwnSent(src: ReplySource, s: OwnSets): boolean {
  const direction = typeof src.direction === 'string' ? lower(src.direction) : '';
  if (direction === 'out' || direction === 'outgoing') return true;
  if (src.emailType === 'sent') return true;
  if (rawFolderIsSent(src.folder) || normalizeEmailFolder(src) === 'sent') return true;

  const from = parseAddresses(src.from);
  if (s.mailbox) {
    // Own copies that landed in inbox/deleted/drafts (direction 'in') still have from == mailbox.
    if (from.some(p => lower(p.address) === s.mailbox)) return true;
    const sender = parseAddresses(src.senderEmail)[0];
    if (sender && lower(sender.address) === s.mailbox) return true;
    // The signed-in owner's own mail sitting in ANOTHER mailbox (the copy Lidia's mailbox got when
    // Ben cc'd her): from is him, so the reply continues with its recipients.
    if (from.length > 0 ? from.every(p => s.self.has(lower(p.address))) : !!sender && s.self.has(lower(sender.address))) return true;
    // Deliberately NOT "from is any own account": mail from lidia@ sitting in ben@'s mailbox is
    // inbound and the reply goes to lidia.
    return false;
  }
  // No mailbox on the doc (legacy IMAP-era): the only evidence left is who it is from.
  return from.length > 0 && from.every(p => s.accounts.has(lower(p.address)) || s.self.has(lower(p.address)));
}

/** True when the owner authored this email (so a reply continues with its recipients). */
export function isSentByOwner(
  src: ReplySource,
  ownAddresses?: Iterable<string | null | undefined>,
  more?: Pick<ReplyRecipientsOptions, 'selfAddresses' | 'accountAddresses'>,
): boolean {
  return computeOwnSent(src, ownSets(src, { ownAddresses, selfAddresses: more?.selfAddresses, accountAddresses: more?.accountAddresses }));
}

/**
 * The To / Cc a Reply or Reply-all should be pre-filled with. To never contains an own address
 * while any other address exists; own addresses are used only for genuinely self-addressed or
 * internal mail (usedOwnFallback). Cc never contains an own address. Bcc is never carried.
 */
export function replyRecipients(src: ReplySource, opts: ReplyRecipientsOptions): ReplyRecipients {
  const sets = ownSets(src, opts);
  const own = sets.own;
  const isOwn = (p: ParsedAddress): boolean => own.has(lower(p.address));
  const notOwn = (list: ParsedAddress[]): ParsedAddress[] => list.filter(p => !isOwn(p));

  const ownSent = computeOwnSent(src, sets);
  const from = parseAddresses(src.from);
  const to = parseAddresses(src.to);
  const cc = parseAddresses(src.cc);

  const candidates = ownSent ? to : from;
  // Tiers, first non-empty wins. The last two only fire for self-addressed / internal mail or
  // when the only real address lives in Cc.
  const tiers: ParsedAddress[][] = [
    notOwn(candidates),
    ownSent ? notOwn(cc) : [],
    candidates,
    cc,
  ];
  let tier = 0;
  while (tier < tiers.length && tiers[tier].length === 0) tier++;
  const toList = tier < tiers.length ? tiers[tier] : [];

  let ccList: ParsedAddress[] = [];
  if (opts.mode === 'replyAll') {
    const pool = ownSent ? cc : to.concat(cc);
    // Also covers "To drawn from the original-Cc tier": those are in toList, so they leave Cc.
    const inTo = new Set(toList.map(p => lower(p.address)));
    ccList = parseAddresses(pool).filter(p => !isOwn(p) && !inTo.has(lower(p.address)));
  }

  const names: Record<string, string> = {};
  from.concat(to, cc).forEach(p => {
    const key = lower(p.address);
    if (p.name && !names[key]) names[key] = p.name;
  });

  return {
    to: toList.map(p => p.address),
    cc: ccList.map(p => p.address),
    names,
    ownSent,
    usedOwnFallback: toList.some(isOwn),
  };
}
